import { PageLayout } from '@/layouts/page-layout'
import { Link, Outlet, useSearchParams } from 'react-router-dom'
import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Input,
  Button,
  Checkbox
} from '@material-tailwind/react'
import { useQueryParamsDefaults } from '@/hooks/use-query-params-defaults'
import { AssetSelector, AttachmentStatusSelector, AttachmentTypeSelector, CompanySelector, ContractSelector, EmployeeSelector } from '@/shared/selectors'
import { useEffect, useState } from 'react'
import { AttachmentTable, EmployeeContractAttachmentTable } from './tables'
import { FilterPopover } from '@/shared/components/filter-popover'
import { list } from '@/router/utils'
import { useAuthorization } from '@/hooks'

const getMonthInputValue = (dateValue = '') => {
  if (!dateValue) return ''

  const [year, month] = dateValue.split('-')

  if (!year || !month) return ''

  return `${year}-${String(month).padStart(2, '0')}`
}

const getFirstDayOfMonth = (monthValue = '') => {
  if (!monthValue) return ''

  const [year, month] = monthValue.split('-').map(Number)

  if (!year || !month) return ''

  return `${year}-${String(month).padStart(2, '0')}-01`
}

const getLastDayOfMonth = (monthValue = '') => {
  if (!monthValue) return ''

  const [year, month] = monthValue.split('-').map(Number)

  if (!year || !month) return ''

  const lastDay = new Date(year, month, 0).getDate()

  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
}

export const AttachmentList = () => {
  const TABS = Object.freeze({
    ATTACHMENTS: 'attachments',
    EMPLOYEE_CONTRACT_ATTACHMENTS: 'employee_contract_attachments'
  })
  useQueryParamsDefaults({
    page: 1,
    per_page: 10
  })

  const [activeTab, setActiveTab] = useState(TABS.ATTACHMENTS)
  const [searchParams, setSearchParams] = useSearchParams()
  const attachmentTypeKlasses = activeTab === TABS.EMPLOYEE_CONTRACT_ATTACHMENTS
    ? ['Employee']
    : ['Contractor::Asset', 'Asset', 'Contractor::Contract', 'Contractor::Company']

  const handleFilter = ({ key, value = '' } = {}) => {
    searchParams.set('page', 1)
    if (!value) {
      searchParams.delete(key)
      setSearchParams(searchParams)
      return
    }

    searchParams.set(key, value)
    setSearchParams(searchParams)
  }

  const onTabChange = (keyTab) => {
    setActiveTab(keyTab)
    const clearSearchParams = new URLSearchParams()
    setSearchParams(clearSearchParams)
  }

  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })

  useEffect(() => {
    hasAccess('attachment#list')
  }, [])

  return (
    <PageLayout
      title='Documentos'
      description='En este módulo puedes tomar acción sobre todos los documentos de todos los módulos.'
      actions={[

        <FilterPopover >

          <div className='grid gap-4'>
            {

              activeTab === TABS.EMPLOYEE_CONTRACT_ATTACHMENTS &&
              <EmployeeSelector
                onChange={(option = {}) => handleFilter({ key: 'employee_id', value: option?.value })}
                value={Number(searchParams.get('employee_id'))}
                includeHistory
              />
            }

            {

              activeTab === TABS.ATTACHMENTS &&
              <AssetSelector
                onChange={(option = {}) => {
                  searchParams.set('page', 1)

                  if (!option?.value) {
                    searchParams.delete('asset_identifier')
                    searchParams.delete('attachable_type')
                    setSearchParams(searchParams)
                    return
                  }

                  searchParams.set('asset_identifier', option.data?.identifier || option.value)
                  if (option.data?.attachableType) {
                    searchParams.set('attachable_type', option.data.attachableType)
                  } else {
                    searchParams.delete('attachable_type')
                  }
                  setSearchParams(searchParams)
                }}
                value={searchParams.get('asset_identifier') || ''}
              />
            }
            <CompanySelector
              onChange={(option = {}) => handleFilter({ key: 'company_id', value: option?.value })}
              value={Number(searchParams.get('company_id'))}
            />

            <ContractSelector
              onChange={(option = {}) => handleFilter({ key: 'contract_id', value: option?.value })}
              value={Number(searchParams.get('contract_id'))}
            />

            <AttachmentTypeSelector
              onChange={(option = {}) => handleFilter({ key: 'attachment_type_id', value: option?.value })}
              value={Number(searchParams.get('attachment_type_id'))}
              klasses={attachmentTypeKlasses}
            />

            <AttachmentStatusSelector
              onChange={(options = []) => {
                searchParams.set('page', 1)
                if (!options.length) {
                  searchParams.delete('status[]')
                  setSearchParams(searchParams)
                  return
                }

                searchParams.delete('status[]')
                options.forEach(option => searchParams.append('status[]', option.value))
                setSearchParams(searchParams)
              }}
              value={searchParams.getAll('status[]')}
            />

            <div className='grid grid-cols-2 gap-2'>
              <div>
                <label className='block text-xs font-medium text-gray-700 mb-1'>
                  Desde (mes)
                </label>
                <input
                  type='month'
                  className='border rounded px-2 py-2 text-sm w-full'
                  value={getMonthInputValue(searchParams.get('range[from]'))}
                  onChange={(evt) =>
                    handleFilter({
                      key: 'range[from]',
                      value: getFirstDayOfMonth(evt.target.value)
                    })}
                />
              </div>

              <div>
                <label className='block text-xs font-medium text-gray-700 mb-1'>
                  Hasta (mes)
                </label>
                <input
                  type='month'
                  className='border rounded px-2 py-2 text-sm w-full'
                  value={getMonthInputValue(searchParams.get('range[to]'))}
                  onChange={(evt) =>
                    handleFilter({
                      key: 'range[to]',
                      value: getLastDayOfMonth(evt.target.value)
                    })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 items-end gap-2">
              <Input
                type='date'
                label='Vencimiento'
                value={searchParams.get('expiration_date') || ''}
                onChange={(evt) =>
                  handleFilter({ key: 'expiration_date', value: evt.target.value })
                }
              />
              <div className="flex items-center gap-2 pt-2">
                <Checkbox
                  label="Por Vencer"
                  checked={searchParams.get('about_to_expire') === 'true'}
                  onChange={(evt) =>
                    handleFilter({ key: 'about_to_expire', value: evt.target.checked ? 'true' : '' })
                  }
                />
              </div>
            </div>
          </div>
          <Link to={list('attachments')}>
            <Button color='red' variant='outlined' className='mt-4'>
              Borrar filtros
            </Button>
          </Link>
        </FilterPopover>
      ]}
    >
      <Tabs value={activeTab}>
        <TabsHeader>
          <Tab value={TABS.ATTACHMENTS} onClickCapture={() => onTabChange(TABS.ATTACHMENTS)}>Activos/Contratistas/Contratos</Tab>
          <Tab value={TABS.EMPLOYEE_CONTRACT_ATTACHMENTS} onClickCapture={() => onTabChange(TABS.EMPLOYEE_CONTRACT_ATTACHMENTS)}>Personas</Tab>
        </TabsHeader>
        <TabsBody>
          <TabPanel value={TABS.ATTACHMENTS}>
            <AttachmentTable
              skip={activeTab === TABS.EMPLOYEE_CONTRACT_ATTACHMENTS}
            />
          </TabPanel>

          <TabPanel value={TABS.EMPLOYEE_CONTRACT_ATTACHMENTS}>
            <EmployeeContractAttachmentTable
              skip={activeTab === TABS.ATTACHMENTS}
            />
          </TabPanel>
        </TabsBody>
      </Tabs>
      <Outlet />
    </PageLayout >

  )
}
